import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { fileMatchesAccept, templateContent } from '@/js/util';

const NEW_INDEX = 'NEW_IMAGE';
const HEIC_PATTERN = /\.(heic|heif)$/i;

export default class extends Controller {
  static targets = ['main', 'mainInput', 'thumbs', 'thumb', 'template', 'addInput'];

  static values = {
    url: String,
    itemId: Number,
    max: Number,
    maxFileSize: Number,
    flash: String,
    typeError: String,
    sizeError: String,
    primaryError: String,
    limitError: String,
    heicPlaceholder: String,
  };

  #serial = 0;
  #pickedFiles = new WeakMap();

  disconnect() {
    this.element.querySelectorAll('img').forEach((image) => this.#release(image));
  }

  replaceMain() {
    this.mainInputTarget.click();
  }

  async mainSelected({ currentTarget }) {
    const source = await this.#preview(currentTarget);
    if (!source) {
      this.#restorePick(this.mainTarget);
      return;
    }

    this.#pickedFiles.set(this.mainTarget, currentTarget.files[0]);
    this.#show(this.mainTarget, source);
  }

  add() {
    this.addInputTarget.click();
  }

  async addSelected({ currentTarget }) {
    const files = [...currentTarget.files];
    currentTarget.value = '';

    const errors = [];
    const accepted = [];
    for (const file of files) {
      const error = this.#fileError(file, currentTarget);
      if (error) {
        errors.push(error);
        continue;
      }

      const source = await this.#source(file);
      if (source) {
        accepted.push([file, source]);
      } else {
        errors.push(this.typeErrorValue);
      }
    }

    const room = Math.max(this.maxValue - this.#liveThumbs().length, 0);
    if (accepted.length > room) {
      errors.unshift(this.limitErrorValue);
      accepted.splice(room).forEach(([, source]) => this.#revoke(source));
    }
    accepted.forEach(([file, source]) => this.#appendThumb(file, source));

    if (errors.length) {
      this.#showError(errors[0]);
    } else {
      this.#clearError();
    }
    this.#refreshCap();
  }

  replace({ currentTarget }) {
    this.#fileInput(this.#thumbFor(currentTarget)).click();
  }

  async thumbSelected({ currentTarget }) {
    const thumb = this.#thumbFor(currentTarget);
    const source = await this.#preview(currentTarget);
    if (!source) {
      this.#restorePick(thumb);
      return;
    }

    this.#pickedFiles.set(thumb, currentTarget.files[0]);
    this.#show(thumb.querySelector('img'), source);
    thumb.classList.add('is-new');
  }

  remove({ currentTarget }) {
    const thumb = this.#thumbFor(currentTarget);
    const destroyInput = thumb.querySelector('input[name$="[_destroy]"]');
    if (destroyInput) {
      destroyInput.value = '1';
      thumb.classList.add('is-removed');
    } else {
      this.#release(thumb.querySelector('img'));
      thumb.remove();
    }
    this.#refreshCap();
  }

  async makePrimary({ currentTarget, params: { imageId } }) {
    const thumb = this.#thumbFor(currentTarget);
    const itemId = this.itemIdValue;
    const before = this.dispatch('before-primary-change', {
      target: document,
      cancelable: true,
      detail: { itemId },
    });
    if (before.defaultPrevented) {
      return;
    }

    this.element.classList.add('is-busy');
    try {
      const body = new FormData();
      body.append('image_id', imageId);
      const response = await new FetchRequest('put', this.urlValue, {
        body,
        responseKind: 'json',
      }).perform();
      if (!response.ok) {
        this.#showError(this.primaryErrorValue);
        return;
      }

      const {
        primary_image_url: primaryImageUrl,
        secondary_image_url: secondaryImageUrl,
        image_versions: imageVersions,
      } = await response.json;
      this.#show(this.mainTarget, primaryImageUrl);
      this.#show(thumb.querySelector('img'), secondaryImageUrl);
      this.#clearError();
      this.dispatch('primary-changed', {
        target: document,
        detail: { itemId, primaryImageUrl, secondaryImageUrl, imageVersions },
      });
    } catch (error) {
      console.error(error);
      this.#showError(this.primaryErrorValue);
    } finally {
      this.element.classList.remove('is-busy');
    }
  }

  #appendThumb(file, source) {
    const thumb = templateContent(this.templateTarget);
    const index = `${Date.now().toString(36)}${this.#serial++}`;
    thumb.querySelectorAll('[name], [id], [for]').forEach((element) => {
      ['name', 'id', 'for'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (value) {
          element.setAttribute(attribute, value.replaceAll(NEW_INDEX, index));
        }
      });
    });

    this.#assign(this.#fileInput(thumb), file);
    this.#pickedFiles.set(thumb, file);
    this.#show(thumb.querySelector('img'), source);
    thumb.classList.add('is-new');
    this.thumbsTarget.append(thumb);
  }

  #restorePick(element) {
    const file = this.#pickedFiles.get(element);
    if (file) {
      const input = element === this.mainTarget ? this.mainInputTarget : this.#fileInput(element);
      this.#assign(input, file);
    }
  }

  #assign(input, file) {
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
  }

  async #preview(input) {
    const file = input.files[0];
    if (!file) {
      return null;
    }

    const error = this.#fileError(file, input);
    if (error) {
      this.#reject(input, error);
      return null;
    }

    const source = await this.#source(file);
    if (!source) {
      this.#reject(input, this.typeErrorValue);
      return null;
    }

    this.#clearError();
    return source;
  }

  #fileError(file, input) {
    if (!fileMatchesAccept(file, input.accept)) {
      return this.typeErrorValue;
    }
    if (file.size > this.maxFileSizeValue) {
      return this.sizeErrorValue;
    }
    return null;
  }

  #source(file) {
    if (HEIC_PATTERN.test(file.name) || /^image\/hei[cf]/.test(file.type)) {
      return Promise.resolve(this.heicPlaceholderValue);
    }

    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => resolve(url);
      image.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      image.src = url;
    });
  }

  #show(image, source) {
    this.#release(image);
    image.src = source;
  }

  #release(image) {
    this.#revoke(image.src);
  }

  #revoke(source) {
    if (source?.startsWith('blob:')) {
      URL.revokeObjectURL(source);
    }
  }

  #reject(input, message) {
    input.value = '';
    this.#showError(message);
  }

  #liveThumbs() {
    return this.thumbTargets.filter((thumb) => !thumb.classList.contains('is-removed'));
  }

  #refreshCap() {
    this.element.classList.toggle('is-full', this.#liveThumbs().length >= this.maxValue);
  }

  #thumbFor(element) {
    return this.thumbTargets.find((thumb) => thumb.contains(element));
  }

  #fileInput(thumb) {
    return thumb.querySelector('input[type="file"]');
  }

  get #flash() {
    return document.getElementById(this.flashValue);
  }

  #showError(message) {
    this.#flash?.dispatchEvent(
      new CustomEvent('flash:show', { detail: { message, level: 'error' } })
    );
  }

  #clearError() {
    this.#flash?.dispatchEvent(new CustomEvent('flash:hide'));
  }
}
